import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import type { HelpFooterColumn, HelpFooterLink } from '@/pages/HelpCenter/types'

interface FooterLinksEditorProps {
  columns: HelpFooterColumn[]
  onChange: (columns: HelpFooterColumn[]) => void
}

// Keep in sync with UpdateHelpCenterFooterTool (MCP).
const MAX_COLUMNS = 5
const MAX_LINKS_PER_COLUMN = 10

export function FooterLinksEditor({ columns, onChange }: FooterLinksEditorProps) {
  const addColumn = () => onChange([...columns, { title: '', links: [{ label: '', url: '' }] }])
  const removeColumn = (ci: number) => onChange(columns.filter((_, i) => i !== ci))
  const setTitle = (ci: number, title: string) =>
    onChange(columns.map((c, i) => (i === ci ? { ...c, title } : c)))

  const addLink = (ci: number) =>
    onChange(columns.map((c, i) => (i === ci ? { ...c, links: [...c.links, { label: '', url: '' }] } : c)))
  const removeLink = (ci: number, li: number) =>
    onChange(columns.map((c, i) => (i === ci ? { ...c, links: c.links.filter((_, j) => j !== li) } : c)))
  const setLink = (ci: number, li: number, patch: Partial<HelpFooterLink>) =>
    onChange(
      columns.map((c, i) =>
        i === ci ? { ...c, links: c.links.map((l, j) => (j === li ? { ...l, ...patch } : l)) } : c,
      ),
    )

  return (
    <div className="space-y-3">
      {columns.map((col, ci) => (
        <div key={ci} className="space-y-2 rounded-lg border border-border p-3">
          <div className="flex items-center gap-2">
            <Input
              value={col.title}
              onChange={e => setTitle(ci, e.target.value)}
              placeholder="Column title (e.g. Product)"
              className="font-medium"
            />
            <Button variant="ghost" size="icon" onClick={() => removeColumn(ci)} aria-label="Remove column">
              <Trash2 className="size-4" />
            </Button>
          </div>
          {col.links.map((link, li) => (
            <div key={li} className="flex items-center gap-2 pl-3">
              <Input value={link.label} onChange={e => setLink(ci, li, { label: e.target.value })} placeholder="Label" className="flex-1" />
              <Input value={link.url} onChange={e => setLink(ci, li, { url: e.target.value })} placeholder="https://…" className="flex-1" />
              <Button variant="ghost" size="icon" onClick={() => removeLink(ci, li)} aria-label="Remove link">
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => addLink(ci)}
            disabled={col.links.length >= MAX_LINKS_PER_COLUMN}
            className="ml-3"
          >
            <Plus className="size-3" /> Add link
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addColumn} disabled={columns.length >= MAX_COLUMNS}>
        <Plus className="size-4" /> Add column
      </Button>
    </div>
  )
}
